import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getMe, register, login, googleLogin, logout, listComments, postComment } from '../lib/userAuthApi';
import './CommentsSection.css';

function formatDate(value) {
  return new Date(value).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

// El script de Google Identity Services solo se inyecta aqui, cuando alguien
// abre el bloque de comentarios -- un lector que solo lee no debe pagar ese
// peso (mismo criterio que AdminLogin.js para el panel).
function useGoogleButton(buttonRef, onCredential) {
  useEffect(() => {
    if (!buttonRef.current) return undefined;
    let cancelled = false;

    function initGoogleButton() {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        setTimeout(initGoogleButton, 200);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: (response) => onCredential(response.credential)
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline', size: 'medium', text: 'signin_with', locale: 'es'
      });
    }

    if (!document.getElementById('google-identity-script')) {
      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    initGoogleButton();

    return () => { cancelled = true; };
  }, [buttonRef, onCredential]);
}

function AuthWidget({ onAuthenticated }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const buttonRef = useRef(null);

  useGoogleButton(buttonRef, async (credential) => {
    try {
      await googleLogin(credential);
      onAuthenticated();
    } catch (err) {
      setError('No se pudo iniciar sesión con Google.');
    }
  });

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    try {
      if (mode === 'register') {
        if (!acceptedTerms) {
          setError('Tienes que aceptar los Términos de uso y la Política de privacidad.');
          return;
        }
        await register(form);
      } else {
        await login(form.email, form.password);
      }
      onAuthenticated();
    } catch (err) {
      setError(err.message || 'No se pudo completar la acción.');
    }
  }

  return (
    <div className="comments-auth">
      <p className="comments-auth__hint">Inicia sesión para dejar un comentario.</p>
      <div ref={buttonRef} />
      <p className="comments-auth__divider">o con email y contraseña</p>
      <form onSubmit={handleSubmit} className="comments-auth__form">
        {mode === 'register' && (
          <div className="comments-auth__row">
            <input placeholder="Nombre" value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} />
            <input placeholder="Apellidos" value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} />
          </div>
        )}
        <input type="email" required placeholder="Email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
        <input type="password" required placeholder="Contraseña" value={form.password} onChange={(e) => updateField('password', e.target.value)} />
        {mode === 'register' && (
          <label className="comments-auth__consent">
            <input type="checkbox" required checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
            <span>
              Acepto los <Link to="/condiciones-de-uso" target="_blank">Términos de uso</Link> y la{' '}
              <Link to="/privacidad" target="_blank">Política de privacidad</Link>
            </span>
          </label>
        )}
        <button type="submit">{mode === 'register' ? 'Crear cuenta' : 'Entrar'}</button>
      </form>
      {error && <p className="comments-auth__error">{error}</p>}
      <button type="button" className="comments-auth__toggle" onClick={() => setMode(mode === 'register' ? 'login' : 'register')}>
        {mode === 'register' ? '¿Ya tienes cuenta? Inicia sesión' : '¿No tienes cuenta? Regístrate'}
      </button>
    </div>
  );
}

function CommentsSection({ contentType, contentId }) {
  const [comments, setComments] = useState([]);
  const [me, setMe] = useState(null);
  const [checkedAuth, setCheckedAuth] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    listComments(contentType, contentId).then((data) => setComments(data.comments));
  }, [contentType, contentId]);

  function checkAuth() {
    getMe()
      .then((data) => setMe(data))
      .catch(() => setMe(null))
      .finally(() => setCheckedAuth(true));
  }

  useEffect(() => {
    checkAuth();
  }, []);

  async function handleLogout() {
    await logout();
    setMe(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSending(true);
    try {
      await postComment(contentType, contentId, body);
      setBody('');
      toast.success('Comentario enviado. Se publicará cuando lo revisemos.');
    } catch (error) {
      toast.error(error.message || 'No se pudo enviar el comentario.');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="comments-section">
      <h3>Comentarios{comments.length > 0 ? ` (${comments.length})` : ''}</h3>

      {comments.length === 0 && <p className="empty-state">Todavía no hay comentarios. Sé el primero en escribir uno.</p>}
      {comments.map((comment) => (
        <div key={comment.id} className="comments-section__item">
          <strong>{comment.authorName}</strong>
          <span className="comments-section__date">{formatDate(comment.createdAt)}</span>
          <p>{comment.body}</p>
        </div>
      ))}

      {checkedAuth && (me ? (
        <form onSubmit={handleSubmit} className="comments-section__form">
          <p className="comments-section__signed-in">
            Comentando como {me.firstName || me.email} · <button type="button" onClick={handleLogout}>Cerrar sesión</button>
          </p>
          <textarea
            required
            rows={3}
            maxLength={1000}
            placeholder="Escribe tu comentario…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <button type="submit" disabled={sending}>{sending ? 'Enviando…' : 'Publicar comentario'}</button>
        </form>
      ) : (
        <AuthWidget onAuthenticated={checkAuth} />
      ))}
    </section>
  );
}

export default CommentsSection;
